import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { MRT_Row } from 'material-react-table';
import { DefaultSearchBar, Loading, ReturnButton } from 'components';
import { useEmissionAllocationById, useNotify } from 'hooks';
import { DimensionAllocation, Site } from 'api';
import { IDataSetupDetailsProps } from '../../types';
import { AddNewColumnButton, AllocationDetailsTable, DateRangeDisplay, ExportCSVButton, TemplateActionsButton } from './components';
import { ConfigurationButton } from './components/configurationButton';
import { handleSavePercentage, generateTableColumns } from './helpers';

export const DataSetupDetails = ({ onBackToOverview, allocationId }: IDataSetupDetailsProps) => {
    const notify = useNotify();
    const { data: allocationSetupData, isFetching, error: allocationDataError } = useEmissionAllocationById(allocationId);
    const [searchValue, setSearchValue] = useState('');
    const [filteredSites, setFilteredSites] = useState<Site[]>([]);
    const [onEditMode, setOnEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dimensionValuesChanged, setDimensionValuesChanged] = useState<DimensionAllocation[]>([]);
    const [isInputActive, setIsInputActive] = useState(false);

    const dimensions = useMemo(
        () => (allocationSetupData?.sites?.[0]?.dimensionAllocations || []).map((da) => da.dimension),
        [allocationSetupData?.sites]
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const handleSavePercentageWrapper = useCallback(
        (row: MRT_Row<Site>, dimension: string, value: string, prevValue: string, inputRef: RefObject<HTMLInputElement>) => {
            const newChange = handleSavePercentage(row, dimension, value, prevValue, inputRef, notify);
            if (newChange) {
                setDimensionValuesChanged((prevChanges) => {
                    const existingChangeIndex = prevChanges.findIndex(
                        (change) => change.siteId === newChange.siteId && change.dimension === newChange.dimension
                    );
                    if (existingChangeIndex !== -1) {
                        const updatedChanges = [...prevChanges];
                        updatedChanges[existingChangeIndex] = newChange;
                        return updatedChanges;
                    }
                    return [...prevChanges, newChange];
                });
            }
        },
        [notify]
    );

    const columns = useMemo(
        () => generateTableColumns({
            allocationId,
            dimensions,
            onEditMode,
            handleSavePercentage: handleSavePercentageWrapper,
            setIsInputActive
        }),
        [allocationId, dimensions, onEditMode, handleSavePercentageWrapper, setIsInputActive]
    );

    useEffect(() => {
        if (allocationSetupData?.sites) {
            const applyChangesToSites = (sites: Site[], changes: any[]) => sites.map((site) => {
                const siteChanges = changes.filter((change) => change.siteId === site.siteId);
                if (siteChanges.length > 0) {
                    const updatedAllocations = site.dimensionAllocations.map((da) => {
                        const change = siteChanges.find((c) => c.dimension === da.dimension);
                        return change ? { ...da, percentage: change.percentage } : da;
                    });
                    return { ...site, dimensionAllocations: updatedAllocations };
                }
                return site;
            });

            const initialFilteredSites = allocationSetupData.sites.filter((site: Site) =>
                site.siteName?.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredSites(applyChangesToSites(initialFilteredSites, dimensionValuesChanged));
        }
    }, [allocationSetupData?.sites, searchValue, dimensionValuesChanged]);

    return (
        <Box sx={{ display: 'flex', flexWrap: "wrap", width: "calc(100vw - 150px)", flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
            <ReturnButton onClick={onBackToOverview} label='Return to Emissions Allocation Overview' />
            {!allocationDataError && (
                (isFetching || isLoading) ? <Loading /> :
                    <Box sx={{ display: 'flex', flexWrap: "wrap", width: "calc(100vw - 150px)", flexDirection: 'column', gap: '20px' }}>
                        <Box sx={{ display: 'flex', mt: '12px', justifyContent: 'space-between' }}>
                            <DateRangeDisplay startDate={allocationSetupData?.startDate || ''} endDate={allocationSetupData?.endDate || ''} />
                            <DefaultSearchBar searchValue={searchValue} onSearchChange={handleSearchChange} placeholder='Search sites...' />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: '11px' }}>
                                <TemplateActionsButton allocationId={allocationId} />
                                <ExportCSVButton allocationId={allocationId} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: '11px' }}>
                                <AddNewColumnButton allocationId={allocationId} />
                                <ConfigurationButton
                                    allocationId={allocationId}
                                    filteredSites={filteredSites}
                                    onEditMode={onEditMode}
                                    setOnEditMode={setOnEditMode}
                                    setIsLoading={setIsLoading}
                                    dimensionValuesChanged={dimensionValuesChanged}
                                    setDimensionValuesChanged={setDimensionValuesChanged}
                                    disabled={dimensions.length === 0 || isInputActive}
                                />
                            </Box>
                        </Box>
                        <AllocationDetailsTable columns={columns} data={filteredSites} />
                    </Box>
            )}
        </Box>
    )
};